import React, { useState, useEffect } from "react";
import loader from "./../images/loader.gif";
import Block from "./Block";
import "./../index.css";
import VersionManager from "./VersionManager";
import { firestore, database, auth } from "./../firebase";
import {
  fetchQuestions,
  saveQuestions,
  updateLastModified,
} from "./../storageOperations";
import StatisticsTab from "./StatisticsTab";
import UsersTab from "./UsersTab";

const Dashboard = (props) => {
  const [online, setOnline] = useState([]);
  const [games, setGames] = useState([]);
  const [stats, setStats] = useState([]);
  const [scores, setScores] = useState({ classic: [], frenzy: [] });
  const [questionStats, setQuestionStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [activeTab, setActiveTab] = useState("scores"); // Default tab
  const [gamesTrend, setGamesTrend] = useState([]);
  const [appEvents, setAppEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [showLinksDropdown, setShowLinksDropdown] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const fetchedQuestions = await fetchQuestions();
      setQuestions(fetchedQuestions);

      const statsTmp = await fetchQuestionStats();
      setQuestionStats(statsTmp);

      // Setup real-time listeners for online users and games
      const unsubscribeOnline = firestore
        .collection("Users")
        .where("isOnline", "==", true)
        .onSnapshot((snapshot) => {
          const onlineTmp = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            onlineTmp.push({
              username: data.username,
              id: doc.id,
              lastOnline: data.lastOnline ? data.lastOnline : null,
              type: data.type,
            });
          });
          setOnline(onlineTmp);
        });

      const unsubscribeGames = database.ref("games").on("value", (snapshot) => {
        const resultObj = snapshot.val();
        const gamesTmp = resultObj
          ? Object.keys(resultObj).map((key) => ({ key, ...resultObj[key] }))
          : [];
        setGames(gamesTmp);

        // Compute trend data (last 30 days)
        const trend = {};
        const now = new Date();
        for (let i = 29; i >= 0; i--) {
          const d = new Date(now);
          d.setDate(d.getDate() - i);
          const dateStr = d.toISOString().split("T")[0];
          trend[dateStr] = 0;
        }

        gamesTmp.forEach((game) => {
          if (game.time) {
            const date = new Date(game.time * 1000).toISOString().split("T")[0];
            if (trend[date] !== undefined) {
              trend[date]++;
            }
          }
        });

        const trendData = Object.keys(trend).map((date) => ({
          name: date.split("-")[2] + "/" + date.split("-")[1],
          count: trend[date],
        }));
        setGamesTrend(trendData);
      });

      const unsubscribeAppEvents = firestore
        .collection("AppEvents")
        .orderBy("timestamp", "desc")
        .limit(1000)
        .onSnapshot((snapshot) => {
          const eventsTmp = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            eventsTmp.push({
              id: doc.id,
              ...data,
              timestamp: data.timestamp ? data.timestamp.toDate() : new Date(),
            });
          });
          setAppEvents(eventsTmp);
        });

      const unsubscribeUsers = firestore
        .collection("Users")
        .orderBy("username")
        .onSnapshot((snapshot) => {
          const usersTmp = [];
          snapshot.forEach((doc) => {
            usersTmp.push({ id: doc.id, ...doc.data() });
          });
          setUsers(usersTmp);
        });

      const scoresTmp = await loadScores();
      setScores(scoresTmp);

      setIsLoading(false);

      // Cleanup function to unsubscribe from real-time updates
      return () => {
        unsubscribeOnline();
        unsubscribeGames();
        unsubscribeAppEvents();
        unsubscribeUsers();
      };
    };

    fetchData();
  }, []);

  const fetchQuestionStats = async () => {
    const statsTmp = {
      totalQuestions: 0,
      correctCount: 0,
      wrongCount: 0,
      generalStats: { total: 0, correct: 0, wrong: 0 },
      harryPotterStats: { total: 0, correct: 0, wrong: 0 },
      questionDifficultyMismatch: [],
    };

    const questionStats = await firestore.collection("QuestionStats").get();

    questionStats.forEach((doc) => {
      const data = doc.data();
      const { difficulty, category } = data;
      const correct_count = data.correct_count || 0;
      const wrong_count = data.wrong_count || 0;

      statsTmp.totalQuestions++;
      statsTmp.correctCount += correct_count;
      statsTmp.wrongCount += wrong_count;

      if (category === "GENERAL") {
        statsTmp.generalStats.total += correct_count + wrong_count;
        statsTmp.generalStats.correct += correct_count;
        statsTmp.generalStats.wrong += wrong_count;
      } else if (category === "HARRY_POTTER") {
        statsTmp.harryPotterStats.total += correct_count + wrong_count;
        statsTmp.harryPotterStats.correct += correct_count;
        statsTmp.harryPotterStats.wrong += wrong_count;
      }

      const totalAnswers = correct_count + wrong_count;
      const correctRatio = totalAnswers ? correct_count / totalAnswers : 0;

      if (true) {
        statsTmp.questionDifficultyMismatch.push({
          id: doc.id,
          difficulty,
          correct_count,
          wrong_count,
          correctRatio,
          category,
        });
      }
    });

    return statsTmp;
  };

  const loadScores = async () => {
    const scores = { classic: [], frenzy: [] };
    const snapshotClassic = await firestore
      .collection("TopClassicScores")
      .get();
    const snapshotFrenzy = await firestore.collection("TopFrenzyScores").get();
    snapshotClassic.forEach((doc) => {
      const data = doc.data();
      scores.classic.push({
        id: doc.id,  // Add document ID for delete operation
        rank: data.Rank,
        score: data.Score,
        userId: data.UserId,
        username: data.Username,
      });
    });
    snapshotFrenzy.forEach((doc) => {
      const data = doc.data();
      scores.frenzy.push({
        id: doc.id,  // Add document ID for delete operation
        rank: data.Rank,
        score: data.Score,
        userId: data.UserId,
        username: data.Username,
      });
    });
    scores.classic.sort((a, b) => a.rank - b.rank);
    scores.frenzy.sort((a, b) => a.rank - b.rank);
    return scores;
  };

  const deleteScore = async (key) => {
    if (window.confirm("בטוח שתרצה למחוק את הניקוד הזה?")) {
      await database.ref("scores/" + key).remove();
    }
  };

  const handleUpdateLastModified = async () => {
    await updateLastModified();
    alert("Last modified timestamp updated.");
  };

  const handleEditUserFromScores = (userId) => {
    const userToEdit = users.find(u => u.id === userId);
    if (userToEdit) {
      setEditingUser(userToEdit);
      setActiveTab("users");
    } else {
      alert("User not found in the users collection.");
    }
  };

  const rebuildHighscores = async () => {
    if (!window.confirm("Are you sure you want to rebuild both highscore tables from the Users collection? This will overwrite the current leaderboards.")) return;

    try {
      setIsLoading(true);
      const batch = firestore.batch();

      // Top 10 Classic & Frenzy
      const classicUsers = [...users].sort((a, b) => (Number(b.classic) || 0) - (Number(a.classic) || 0)).slice(0, 10);
      const frenzyUsers = [...users].sort((a, b) => (Number(b.frenzy) || 0) - (Number(a.frenzy) || 0)).slice(0, 10);

      // Fetch existing for deletion
      const classicSnap = await firestore.collection("TopClassicScores").get();
      classicSnap.forEach(doc => batch.delete(doc.ref));

      const frenzySnap = await firestore.collection("TopFrenzyScores").get();
      frenzySnap.forEach(doc => batch.delete(doc.ref));

      // Build new Classic
      classicUsers.forEach((u, index) => {
        const ref = firestore.collection("TopClassicScores").doc(u.id);
        batch.set(ref, {
          Rank: index + 1,
          Score: Number(u.classic) || 0,
          UserId: u.id,
          Username: u.username || "Unknown"
        });
      });

      // Build new Frenzy
      frenzyUsers.forEach((u, index) => {
        const ref = firestore.collection("TopFrenzyScores").doc(u.id);
        batch.set(ref, {
          Rank: index + 1,
          Score: Number(u.frenzy) || 0,
          UserId: u.id,
          Username: u.username || "Unknown"
        });
      });

      await batch.commit();

      const newScores = await loadScores();
      setScores(newScores);
      alert("Highscores successfully rebuilt!");
    } catch (error) {
      console.error("Error rebuilding highscores:", error);
      alert("Error rebuilding highscores: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = () => {
    auth.signOut();
  };

  return isLoading ? (
    <div className="dashboard">
      <img src={loader} alt="Loading..." />
    </div>
  ) : (
    <div className="dashboard">
      <div className="dashboard-header">
        <img src="/trivia-logo.png" alt="Trivia Logo" className="dashboard-logo" />
        <h1 className="dashboard-title">Trivia Dashboard</h1>
        <div className="header-actions">
          <div className="links-dropdown-container">
            <button
              className="header-link dropdown-toggle"
              onClick={() => setShowLinksDropdown(!showLinksDropdown)}
              onBlur={() => setTimeout(() => setShowLinksDropdown(false), 200)}
            >
              <ion-icon name="link-outline"></ion-icon> External Links
              <ion-icon name={showLinksDropdown ? "chevron-up-outline" : "chevron-down-outline"}></ion-icon>
            </button>

            {showLinksDropdown && (
              <div className="links-dropdown">
                <div className="dropdown-section">
                  <span className="section-title">Developers</span>
                  <a href="https://github.com/KupermanL/Trivia-Israel-Unity" target="_blank" rel="noopener noreferrer">
                    <ion-icon name="logo-github"></ion-icon> Unity App GitHub
                  </a>
                  <a href="https://github.com/KupermanL/Trivia-Israel-Admin-Panel" target="_blank" rel="noopener noreferrer">
                    <ion-icon name="logo-github"></ion-icon> Admin Panel GitHub
                  </a>
                </div>

                <div className="dropdown-section">
                  <span className="section-title">Consoles</span>
                  <a href="https://console.firebase.google.com/u/0/project/trivia-rn/overview" target="_blank" rel="noopener noreferrer" className="firebase-link">
                    <ion-icon name="flame-outline"></ion-icon> Firebase Console
                  </a>
                  <a href="https://play.google.com/console/u/0/developers/5901730052861619225/app/4972940488353854284/app-dashboard" target="_blank" rel="noopener noreferrer" className="console-link">
                    <ion-icon name="desktop-outline"></ion-icon> Play Console
                  </a>
                </div>

                <div className="dropdown-section">
                  <span className="section-title">Ads (AdMob)</span>
                  <a href="https://admob.google.com/v2/apps/3758376177/overview?pli=1" target="_blank" rel="noopener noreferrer" className="admob-android-link">
                    <ion-icon name="logo-google"></ion-icon> AdMob Android
                  </a>
                  <a href="https://admob.google.com/v2/apps/3472931799/overview?pli=1" target="_blank" rel="noopener noreferrer" className="admob-ios-link">
                    <ion-icon name="logo-apple"></ion-icon> AdMob iOS
                  </a>
                </div>

                <div className="dropdown-section">
                  <span className="section-title">Stores</span>
                  <a href="https://play.google.com/store/apps/details?id=com.kupi.israel_trivia" target="_blank" rel="noopener noreferrer" className="store-link">
                    <ion-icon name="logo-google-playstore"></ion-icon> Play Store
                  </a>
                  <a href="https://apps.apple.com/il/app/%D7%98%D7%A8%D7%99%D7%95%D7%95%D7%99%D7%94-%D7%99%D7%A9%D7%A8%D7%90%D7%9C/id6499443529" target="_blank" rel="noopener noreferrer" className="store-link" style={{ color: '#2563eb' }}>
                    <ion-icon name="logo-apple"></ion-icon> App Store
                  </a>
                </div>
              </div>
            )}
          </div>

          <button className="sign-out-btn" onClick={handleSignOut}>
            <ion-icon name="log-out-outline"></ion-icon> Sign Out
          </button>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === "scores" ? "active" : ""}`}
            onClick={() => setActiveTab("scores")}
          >
            <ion-icon name="trophy-outline"></ion-icon> ניקודים
          </button>
          <button
            className={`tab ${activeTab === "questions" ? "active" : ""}`}
            onClick={() => setActiveTab("questions")}
          >
            <ion-icon name="help-circle-outline"></ion-icon> שאלות
          </button>
          <button
            className={`tab ${activeTab === "games" ? "active" : ""}`}
            onClick={() => setActiveTab("games")}
          >
            <ion-icon name="game-controller-outline"></ion-icon> משחקים
          </button>
          <button
            className={`tab ${activeTab === "online" ? "active" : ""}`}
            onClick={() => setActiveTab("online")}
          >
            <ion-icon name="people-outline"></ion-icon> מחוברים ({online.length})
          </button>
          <button
            className={`tab ${activeTab === "users" ? "active" : ""}`}
            onClick={() => setActiveTab("users")}
          >
            <ion-icon name="person-circle-outline"></ion-icon> משתמשים
          </button>
          <button
            className={`tab ${activeTab === "statistics" ? "active" : ""}`}
            onClick={() => setActiveTab("statistics")}
          >
            <ion-icon name="stats-chart-outline"></ion-icon> סטטיסטיקה
          </button>
          <button
            className={`tab ${activeTab === "versions" ? "active" : ""}`}
            onClick={() => setActiveTab("versions")}
          >
            <ion-icon name="phone-portrait-outline"></ion-icon> גרסאות
          </button>
        </div>
      </div>

      <div className="tab-content">
        {activeTab === "online" && <Block type="online" data={online} />}
        {activeTab === "games" && <Block type="games" data={games} gamesTrend={gamesTrend} />}
        {activeTab === "scores" && (
          <Block
            type="scores"
            data={scores}
            deleteScore={deleteScore}
            onEditUser={handleEditUserFromScores}
            onRebuildHighscores={rebuildHighscores}
          />
        )}
        {activeTab === "questions" && (
          <Block
            type="questions"
            data={questionStats}
            questions={questions}
            setQuestions={setQuestions}
            handleUpdateLastModified={handleUpdateLastModified}
          />
        )}
        {activeTab === "statistics" && (
          <StatisticsTab
            appEvents={appEvents}
            questionStats={questionStats}
            questions={questions}
            gamesTrend={gamesTrend}
          />
        )}
        {activeTab === "users" && (
          <UsersTab
            users={users}
            online={online}
            editingUser={editingUser}
            setEditingUser={setEditingUser}
          />
        )}
        {activeTab === "versions" && <VersionManager />}
      </div>
    </div>
  );
};

export default Dashboard;
